import type {
  HeroKpiVisibility,
  ReportSectionDto,
  UpdateReportSectionRequestDto,
} from '@act/shared/data-transfer-objects';
import {
  accumulateHeroKpiVisibilityPatch,
  mergeHeroKpiVisibility,
} from '@act/shared/data-transfer-objects';

/** Input for queueing a hero KPI visibility persist. */
export interface QueueHeroKpiVisibilityChangeInput {
  section: ReportSectionDto;
  latestSections: ReportSectionDto[];
  patch: Partial<HeroKpiVisibility>;
  pendingBySection: Record<string, Partial<HeroKpiVisibility>>;
  timers: Record<string, ReturnType<typeof setTimeout>>;
  debounceMs: number;
  updateSectionLocal: (
    sectionRowId: string,
    patch: Partial<ReportSectionDto>,
  ) => void;
  persistSection: (
    sectionRowId: string,
    body: UpdateReportSectionRequestDto,
  ) => void;
}

/**
 * Applies a KPI toggle locally and queues a partial persist patch.
 * Rapid toggles coalesce into one PATCH that includes only changed keys.
 * @param input - Section row, patch, and persist callbacks.
 * @returns Updated local visibility map.
 */
export const queueHeroKpiVisibilityChange = (
  input: QueueHeroKpiVisibilityChangeInput,
): HeroKpiVisibility => {
  const sectionId = input.section.id;
  const persistPatch = accumulateHeroKpiVisibilityPatch(
    input.pendingBySection[sectionId],
    input.patch,
  );
  input.pendingBySection[sectionId] = persistPatch;
  const latest =
    input.latestSections.find(row => row.id === sectionId) ?? input.section;
  const heroKpiVisibility = mergeHeroKpiVisibility(
    latest.heroKpiVisibility,
    persistPatch,
  );
  input.updateSectionLocal(sectionId, { heroKpiVisibility });

  const key = `${sectionId}:heroKpiVisibility`;
  const pendingTimer = input.timers[key];
  if (pendingTimer) {
    clearTimeout(pendingTimer);
  }
  input.timers[key] = setTimeout(() => {
    const queued = input.pendingBySection[sectionId];
    if (queued) {
      input.persistSection(sectionId, { heroKpiVisibility: queued });
      delete input.pendingBySection[sectionId];
    }
    delete input.timers[key];
  }, input.debounceMs);

  return heroKpiVisibility;
};
